import { Card, CardContent } from '@bpinternal/ui-kit-next'
import { useState, type FC } from 'react'
import {
  addDailyAverage,
  collapseDailyUsageToTopN,
  DailySummary,
  fillMissingDates,
  flattenBreakdownWithCumulative,
  FlattenedDailySummary,
} from '../utils'
import { DailySpendChart } from './DailySpendChart'
import { DailySpendChartHeader, SpendType } from './DailySpendChartHeader'

type Props = {
  data?: DailySummary[]
  selectedMonth: Date
}

export const CumulativeAiSpend: FC<Props> = ({ data, selectedMonth }) => {
  const [activeChart, setActiveChart] = useState<SpendType>('total')
  const totalAiSpend = data?.reduce((acc, { value }) => acc + value, 0) ?? 0

  const sortedData = fillMissingDates(
    [...(data ?? [])].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    selectedMonth
  )

  const dataPerBot = flattenBreakdownWithCumulative(sortedData, 'bots')

  const averageAiSpendPerDay = totalAiSpend ? totalAiSpend / (sortedData?.length ?? 1) : 0

  const { grouped: collapsedData, visibleKeys: botKeys } = collapseDailyUsageToTopN(
    dataPerBot,
    5, // top N bots
    0.01 // min percent of total spend to be included
  )

  const dataWithProjection = addDailyAverage(collapsedData, selectedMonth, averageAiSpendPerDay)

  const dataWithProjectedCumulative: FlattenedDailySummary[] = dataWithProjection.map((item, index) => ({
    ...item,
    ...(item.value !== undefined
      ? { cumulative: sortedData.slice(0, index + 1).reduce((acc, { value }) => acc + value, 0) }
      : {}),
    projectedCumulative: (index + 1) * averageAiSpendPerDay,
  }))

  return (
    <Card>
      <DailySpendChartHeader
        totalAiSpend={totalAiSpend}
        averageAiSpendPerDay={averageAiSpendPerDay}
        onActiveChartChange={(type) => setActiveChart(type)}
      />
      <CardContent className="p-2 sm:p-6">
        <DailySpendChart
          data={dataWithProjectedCumulative}
          selectedMonth={selectedMonth}
          activeChart={activeChart}
          visibleKeys={botKeys}
        />
      </CardContent>
    </Card>
  )
}
